import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { NAV_LINKS, SITE } from "../../lib/config";

const Wordmark = ({ onDark = false, onClick }) => (
  <Link
    to="/"
    onClick={onClick}
    data-testid="nav-logo"
    className="flex items-center gap-2.5 select-none"
    aria-label="Ibex Acquisitions & Development — home"
  >
    <img
      src={SITE.logoUrl}
      alt=""
      aria-hidden="true"
      className="h-11 w-auto md:h-12 shrink-0"
      style={{
        filter: onDark
          ? "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"
          : "none",
      }}
    />
    <span className="flex flex-col leading-tight">
      <span
        className="font-heading tracking-tight text-xl md:text-[1.5rem]"
        style={{
          color: onDark ? "#FAF7F2" : "#122B20",
          letterSpacing: "-0.01em",
        }}
      >
        IBEX
      </span>
      <span
        className="text-[0.55rem] md:text-[0.6rem] mt-0.5"
        style={{
          color: onDark ? "rgba(250,247,242,0.72)" : "#A8A29A",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        Acquisitions &amp; Development
      </span>
    </span>
  </Link>
);

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isBuilders = location.pathname.startsWith("/builders");
  const isSubpage = location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Nav visual state: transparent over hero on both routes (both have a dark hero).
  const overDarkHero = !scrolled;

  const scrollToOffer = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isBuilders) {
      // Builders page has its own conversion form: #buy-box
      const el = document.getElementById("buy-box");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      const el = document.getElementById("offer-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Handles anchor links so that clicking (e.g.) "How It Works" while on /builders
  // routes back to "/" and then scrolls to the anchor.
  const handleAnchorClick = (href) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isSubpage) {
      navigate("/" + href);
    } else {
      const el = document.getElementById(href.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleRouteClick = () => setMobileOpen(false);

  const ctaLabel = isBuilders ? "Submit Your Buy Box" : "Get My Cash Offer";

  const renderLink = (link, keyPrefix) => {
    if (link.to) {
      const active = location.pathname === link.to;
      return (
        <Link
          key={link.to}
          to={link.to}
          onClick={handleRouteClick}
          data-testid={`${keyPrefix}-link-${link.to.replace("/", "")}`}
          className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
            overDarkHero
              ? active
                ? "text-[#C6A15B]"
                : "text-[#FAF7F2]/90 hover:text-[#C6A15B]"
              : active
              ? "text-[#1B4332]"
              : "text-[#2B2926] hover:text-[#1B4332]"
          }`}
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.href}
        href={isSubpage ? "/" + link.href : link.href}
        onClick={handleAnchorClick(link.href)}
        data-testid={`${keyPrefix}-link-${link.href.replace("#", "")}`}
        className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
          overDarkHero
            ? "text-[#FAF7F2]/90 hover:text-[#C6A15B]"
            : "text-[#2B2926] hover:text-[#1B4332]"
        }`}
      >
        {link.label}
      </a>
    );
  };

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "nav-glass" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className={`flex items-center justify-between ${scrolled ? "py-3" : "py-5"} transition-all duration-300`}>
            <Wordmark onDark={overDarkHero} onClick={handleRouteClick} />

            <nav className="hidden lg:flex items-center gap-9">
              {NAV_LINKS.map((link) => renderLink(link, "nav"))}
            </nav>

            <div className="hidden lg:block">
              <button
                onClick={scrollToOffer}
                className="btn-primary"
                data-testid="nav-cta-desktop"
              >
                {ctaLabel}
              </button>
            </div>

            <button
              className="lg:hidden p-2 -mr-2"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              data-testid="nav-menu-open"
              style={{ color: overDarkHero ? "#FAF7F2" : "#122B20" }}
            >
              <Menu size={26} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] lg:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0"
              style={{
                background: "rgba(18,43,32,0.9)",
                backdropFilter: "blur(20px) saturate(140%)",
                WebkitBackdropFilter: "blur(20px) saturate(140%)",
              }}
            />
            <div className="relative h-full flex flex-col px-6 py-6">
              <div className="flex items-center justify-between">
                <Wordmark onDark onClick={handleRouteClick} />
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  data-testid="nav-menu-close"
                  className="p-2 -mr-2 text-[#FAF7F2]"
                >
                  <X size={26} strokeWidth={1.5} />
                </button>
              </div>
              <nav className="flex-1 flex flex-col justify-center gap-6">
                {NAV_LINKS.map((link, i) => {
                  const el = link.to ? (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={handleRouteClick}
                      data-testid={`mobile-nav-link-${link.to.replace("/", "")}`}
                      className="font-heading text-4xl text-[#FAF7F2] hover:text-[#C6A15B] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.href}
                      href={isSubpage ? "/" + link.href : link.href}
                      onClick={handleAnchorClick(link.href)}
                      data-testid={`mobile-nav-link-${link.href.replace("#", "")}`}
                      className="font-heading text-4xl text-[#FAF7F2] hover:text-[#C6A15B] transition-colors"
                    >
                      {link.label}
                    </a>
                  );
                  return (
                    <motion.div
                      key={link.to || link.href}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
                    >
                      {el}
                    </motion.div>
                  );
                })}
              </nav>
              <button
                onClick={scrollToOffer}
                className="btn-primary w-full"
                data-testid="mobile-nav-cta"
              >
                {ctaLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
